import { WIZARD_SPELLBOOK_ITEM_DEF_ID } from "@/lib/rules/dnd5e/character/wizard-spellbook";

export const BUNDLE_ITEM_IDS = [
  // already exist
  "item_chain_mail",
  "item_leather_armor",
  "item_scale_mail",
  "item_shield",
  "item_longsword",
  "item_shortbow",
  "item_longbow",
  "item_mace",
  "item_rapier",
  "item_shortsword",
  "item_holy_symbol",
  "item_thieves_tools",
  "item_greataxe",
  "item_greatsword",
  "item_quarterstaff",
  "item_warhammer",
  "item_light_crossbow",
  "item_dagger",
  "item_scimitar",
  WIZARD_SPELLBOOK_ITEM_DEF_ID,
  // newly authored (Tasks 1-2 of this branch)
  "item_handaxe",
  "item_javelin",
  "item_sickle",
  "item_spear",
  "item_flail",
  "item_chain_shirt",
  "item_studded_leather_armor",
  "item_explorers_pack",
  "item_entertainers_pack",
  "item_priests_pack",
  "item_dungeoneers_pack",
  "item_burglars_pack",
  "item_scholars_pack",
  "item_quiver_and_arrows",
  "item_arcane_focus_crystal",
  "item_arcane_focus_orb",
  "item_arcane_focus_quarterstaff",
  "item_druidic_focus_quarterstaff",
  "item_druidic_focus_sprig_of_mistletoe",
  "item_herbalism_kit",
  "item_robe",
  "item_book",
  "item_parchment",
  "item_travelers_clothes",
  "item_crowbar",
  "item_pouch",
  "item_calligraphers_supplies",
  "item_musical_instrument_generic",
  "item_woodcarvers_tools",
] as const;

export type BundleItemId = (typeof BUNDLE_ITEM_IDS)[number];

export type Bundle = {
  readonly items: readonly BundleItemId[];
  readonly goldGp: number;
};

const LABEL_PART_SEPARATOR_RE = /[^a-z0-9]+/i;
const ITEM_PREFIX_RE = /^item_/;
const PROFILE_5E_PREFIX_RE = /^profile_5e_/;
const DEF_5E_PREFIX_RE = /^def_5e-/;

export const bundleItemLabel = (itemId: string): string =>
  itemId
    .replace(ITEM_PREFIX_RE, "")
    .replace(PROFILE_5E_PREFIX_RE, "")
    .replace(DEF_5E_PREFIX_RE, "")
    .split(LABEL_PART_SEPARATOR_RE)
    .filter(Boolean)
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(" ");

export const bundleDescription = (
  items: readonly string[],
  goldGp: number
): string => {
  const counts = new Map<string, number>();
  for (const itemId of items) {
    counts.set(itemId, (counts.get(itemId) ?? 0) + 1);
  }
  const labels = [...counts.entries()].map(([itemId, count]) =>
    count > 1 ? `${bundleItemLabel(itemId)} x${count}` : bundleItemLabel(itemId)
  );
  if (goldGp > 0) {
    labels.push(`${goldGp} GP`);
  }
  return labels.length > 0 ? labels.join(", ") : "Starting gold only.";
};

export const CLASS_BUNDLES: Record<
  | "fighter"
  | "cleric"
  | "rogue"
  | "barbarian"
  | "monk"
  | "paladin"
  | "bard"
  | "warlock"
  | "druid"
  | "sorcerer"
  | "wizard"
  | "ranger",
  readonly Bundle[]
> = {
  barbarian: [
    {
      items: [
        "item_greataxe",
        "item_handaxe",
        "item_handaxe",
        "item_handaxe",
        "item_handaxe",
        "item_explorers_pack",
      ],
      goldGp: 15,
    },
    { items: [], goldGp: 75 },
  ],
  bard: [
    {
      items: [
        "item_leather_armor",
        "item_dagger",
        "item_dagger",
        "item_musical_instrument_generic",
        "item_entertainers_pack",
      ],
      goldGp: 19,
    },
    { items: [], goldGp: 90 },
  ],
  cleric: [
    {
      items: [
        "item_chain_shirt",
        "item_shield",
        "item_mace",
        "item_holy_symbol",
        "item_priests_pack",
      ],
      goldGp: 7,
    },
    { items: [], goldGp: 110 },
  ],
  druid: [
    {
      items: [
        "item_leather_armor",
        "item_shield",
        "item_sickle",
        "item_druidic_focus_quarterstaff",
        "item_explorers_pack",
        "item_herbalism_kit",
      ],
      goldGp: 9,
    },
    { items: [], goldGp: 50 },
  ],
  fighter: [
    {
      items: [
        "item_chain_mail",
        "item_greatsword",
        "item_flail",
        "item_javelin",
        "item_javelin",
        "item_javelin",
        "item_javelin",
        "item_javelin",
        "item_javelin",
        "item_javelin",
        "item_javelin",
        "item_dungeoneers_pack",
      ],
      goldGp: 4,
    },
    {
      items: [
        "item_studded_leather_armor",
        "item_scimitar",
        "item_shortsword",
        "item_longbow",
        "item_quiver_and_arrows",
        "item_dungeoneers_pack",
      ],
      goldGp: 11,
    },
    { items: [], goldGp: 155 },
  ],
  monk: [
    {
      items: [
        "item_spear",
        "item_dagger",
        "item_dagger",
        "item_dagger",
        "item_dagger",
        "item_dagger",
        // SRD 5.2.1: "Artisan's Tools or Musical Instrument chosen for the
        // tool proficiency above" — the tool-proficiency choice mechanism
        // isn't modeled yet (see 2026-07-16-monk-tool-proficiency-choice-not-modeled,
        // vault), so this bundle hardcodes one concrete kit (Woodcarver's
        // Tools) rather than the unresolvable generic placeholder it used
        // to reference. Replace once the real choice mechanism exists.
        "item_woodcarvers_tools",
        "item_explorers_pack",
      ],
      goldGp: 11,
    },
    { items: [], goldGp: 50 },
  ],
  paladin: [
    {
      items: [
        "item_chain_mail",
        "item_shield",
        "item_longsword",
        "item_javelin",
        "item_javelin",
        "item_javelin",
        "item_javelin",
        "item_javelin",
        "item_javelin",
        "item_holy_symbol",
        "item_priests_pack",
      ],
      goldGp: 9,
    },
    { items: [], goldGp: 150 },
  ],
  ranger: [
    {
      items: [
        "item_studded_leather_armor",
        "item_scimitar",
        "item_shortsword",
        "item_longbow",
        "item_quiver_and_arrows",
        "item_druidic_focus_sprig_of_mistletoe",
        "item_explorers_pack",
      ],
      goldGp: 7,
    },
    { items: [], goldGp: 150 },
  ],
  rogue: [
    {
      items: [
        "item_leather_armor",
        "item_dagger",
        "item_dagger",
        "item_shortsword",
        "item_shortbow",
        "item_quiver_and_arrows",
        "item_thieves_tools",
        "item_burglars_pack",
      ],
      goldGp: 8,
    },
    { items: [], goldGp: 100 },
  ],
  sorcerer: [
    {
      items: [
        "item_spear",
        "item_dagger",
        "item_dagger",
        "item_arcane_focus_crystal",
        "item_dungeoneers_pack",
      ],
      goldGp: 28,
    },
    { items: [], goldGp: 50 },
  ],
  warlock: [
    {
      items: [
        "item_leather_armor",
        "item_sickle",
        "item_dagger",
        "item_dagger",
        "item_arcane_focus_orb",
        "item_book",
        "item_scholars_pack",
      ],
      goldGp: 15,
    },
    { items: [], goldGp: 100 },
  ],
  wizard: [
    {
      items: [
        "item_dagger",
        "item_dagger",
        "item_arcane_focus_quarterstaff",
        "item_robe",
        WIZARD_SPELLBOOK_ITEM_DEF_ID,
        "item_scholars_pack",
      ],
      goldGp: 5,
    },
    { items: [], goldGp: 55 },
  ],
};

export const BACKGROUND_BUNDLES: Record<
  "acolyte" | "criminal" | "sage",
  readonly Bundle[]
> = {
  acolyte: [
    {
      items: [
        "item_calligraphers_supplies",
        "item_book",
        "item_holy_symbol",
        "item_parchment",
        "item_robe",
      ],
      goldGp: 8,
    },
    { items: [], goldGp: 50 },
  ],
  criminal: [
    {
      items: [
        "item_dagger",
        "item_dagger",
        "item_thieves_tools",
        "item_crowbar",
        "item_pouch",
        "item_pouch",
        "item_travelers_clothes",
      ],
      goldGp: 16,
    },
    { items: [], goldGp: 50 },
  ],
  sage: [
    {
      items: [
        "item_quarterstaff",
        "item_calligraphers_supplies",
        "item_book",
        "item_parchment",
        "item_robe",
      ],
      goldGp: 8,
    },
    { items: [], goldGp: 50 },
  ],
};

export const bundlesForBackground = (
  backgroundId?: string | null
): readonly Bundle[] => {
  if (!backgroundId) {
    return [];
  }
  const bundles =
    BACKGROUND_BUNDLES[backgroundId as keyof typeof BACKGROUND_BUNDLES];
  return Array.isArray(bundles) ? bundles : [];
};

export type ResolveBundleResult =
  | { readonly items: readonly BundleItemId[]; readonly goldGp: number }
  | { readonly error: string };

const AMMO_PROFILE_BY_ITEM_ID = {
  item_hand_crossbow: "profile_5e_ammunition_bolts_20",
  item_light_crossbow: "profile_5e_ammunition_bolts_20",
  item_longbow: "profile_5e_ammunition_arrows_20",
  item_shortbow: "profile_5e_ammunition_arrows_20",
} as const;

export function resolveBundle(
  cls: string,
  choice: "a" | "b" | "c",
  backgroundChoice?: "a" | "b",
  background?: string
): ResolveBundleResult {
  const classOptions = CLASS_BUNDLES[cls as keyof typeof CLASS_BUNDLES];
  if (!classOptions) {
    return { error: `Unsupported class '${cls}'.` };
  }
  const choiceIndex = choice === "a" ? 0 : choice === "b" ? 1 : 2;
  if (choiceIndex >= classOptions.length) {
    return {
      error: `Class '${cls}' has no option '${choice}' (only ${classOptions.length} option(s) exist).`,
    };
  }
  const classBundle = classOptions[choiceIndex];

  const bgOptions = bundlesForBackground(background);
  const bgBundle = bgOptions?.[backgroundChoice === "b" ? 1 : 0];

  return {
    items: [...classBundle.items, ...(bgBundle?.items ?? [])],
    goldGp: classBundle.goldGp + (bgBundle?.goldGp ?? 0),
  };
}

export function resolveBundleAmmoProfiles(
  bundle: readonly string[]
): readonly string[] {
  const profiles = new Set<string>();
  for (const itemId of bundle) {
    const profileId =
      AMMO_PROFILE_BY_ITEM_ID[itemId as keyof typeof AMMO_PROFILE_BY_ITEM_ID];
    if (profileId) {
      profiles.add(profileId);
    }
  }
  return [...profiles];
}
